import { useOidc } from '@axa-fr/react-oidc';
import MenuIcon from '@mui/icons-material/Menu';
import AppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import { styled, useTheme } from '@mui/material/styles';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import NotificationBell from 'components/Layout/NotificationBell';
import { buildSearchWithScenario, useScenario } from 'modules/project/providers/scenario';
import { resetAnalytics, trackEvent } from 'providers/analytics';
import { useAuthorisation } from 'providers/authorisation';
import { usePage } from 'providers/page';
import React from 'react';
import { Link as RouterLink, useLocation, useParams } from 'react-router-dom';
import { MenuItem, Select } from '@mui/material';
import useRoles from 'hooks/useRoles';
import { isLabOnlyUser } from 'utils/roles';
import AccountMenu from './AccountMenu';
import BackNavButton from './BackNavButton';
import UserFeedbackIcon from './UserFeedback';
import { DRAWER_WIDTH } from './utils';

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const StyledAppBar = styled(AppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme }) => ({
  background: theme.palette.primary.main,
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  variants: [
    {
      props: ({ open }) => open,
      style: {
        marginLeft: DRAWER_WIDTH,
        width: `calc(100% - ${DRAWER_WIDTH}px)`,
        transition: theme.transitions.create(['width', 'margin'], {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
      },
    },
  ],
}));

type TopBarProps = {
  open: boolean;
  openMenu: () => void;
  paddingLeft: string;
  toggleLocale: (newLocale: string) => void;
  locale: string;
};

const TopBar = ({ open, openMenu, paddingLeft, toggleLocale, locale }: TopBarProps) => {
  const { isAuthenticated, logout } = useOidc();
  const { state: { user, loading: authoriseLoading } } = useAuthorisation();
  const { state: pageState } = usePage();
  const location = useLocation();
  const { entityId, projectId } = useParams<{ entityId?: string; projectId?: string }>();
  const searchProjectId = new URLSearchParams(location.search).get('projectId') ?? undefined;
  const pathProjectId = location.pathname.match(/\/projects\/([a-f0-9]{20,}[^/]*)/i)?.[1]
    ?? location.pathname.match(/\/public\/project\/([a-f0-9]{20,}[^/]*)/i)?.[1];
  const currentProjectId = entityId ?? projectId ?? searchProjectId ?? pathProjectId;
  const currentUrl = `${location.pathname}${location.search}${location.hash}`;
  const { activeScenarioId } = useScenario();
  const tabSearch = buildSearchWithScenario('', activeScenarioId);

  const theme = useTheme();
  const isLarge = useMediaQuery(theme.breakpoints.up('sm'));
  const roles = useRoles();
  const isLabOnly = isLabOnlyUser(roles) && !user?.permissions?.teamRoles?.length;
  const topBarShouldDisplay = !authoriseLoading && (user || !isAuthenticated) && !(!isLarge && open);

  const handleLogout = () => {
    trackEvent('user_logged_out');
    resetAnalytics();
    logout('/app');
  };

  const isPartialMatch = (path: string) => {
    const pattern = path.replace(/\*/g, '[^/]+');
    const regex = new RegExp(`^${pattern}`);
    return regex.test(location.pathname);
  };

  return (
    <>
      <StyledAppBar
        position="fixed"
        open={open}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={() => openMenu()}
            edge="start"
            sx={[
              {
                marginRight: 5,
              },
              open && { display: 'none' },
            ]}
          >
            <MenuIcon />
          </IconButton>
          {topBarShouldDisplay && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
              }}
            >
              <Box
                sx={{
                  flex: '1',
                }}
              >
                {!isLabOnly && pageState.parentPath && (
                  <BackNavButton path={pageState.parentPath!} text={pageState.parentTitle} isLarge={isLarge} />
                )}
              </Box>
              <Box><Typography variant="h6">{pageState.title}</Typography></Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row-reverse',
                  flex: '1',
                }}
              >
                {React.Children.map(pageState.topBarActions, (action) => (
                  <Box sx={{ m: 1, mr: 1 }}>
                    {action}
                  </Box>
                ))}
                <AccountMenu logout={handleLogout} isLarge={isLarge} name={user.name} />
                <Box sx={{ m: 1, mr: 2 }}>
                  <Select
                    value={locale} // Ensure the correct locale is selected in the dropdown
                    onChange={(event) => toggleLocale(event.target.value)} // Update locale on change
                    variant="standard"
                    sx={{ color: 'white', '&::before': { borderBottomColor: 'white' }, '& svg': { color: 'white' } }}
                  >
                    <MenuItem value="en">EN</MenuItem>
                    <MenuItem value="es">ES</MenuItem>
                    <MenuItem value="fr">FR</MenuItem>
                    <MenuItem value="ru">RU</MenuItem>
                    <MenuItem value="pt">PT</MenuItem>
                    <MenuItem value="tr">TR</MenuItem>
                    <MenuItem value="gr">GR</MenuItem>
                    {/* Add more languages as needed */}
                  </Select>
                </Box>
                <Box sx={{ m: 1, mr: 1 }}>
                  <UserFeedbackIcon
                    userId={user.id}
                    userName={user.name}
                    userEmail={user.email}
                    currentUrl={currentUrl}
                    projectId={currentProjectId}
                  />
                </Box>
                <Box sx={{ m: 1, mr: 1 }}>
                  <NotificationBell />
                </Box>
              </Box>
            </Box>
          )}
        </Toolbar>
      </StyledAppBar>

      {pageState.subNavigation.length > 0 && (
        <Box
          component={Paper}
          square
          sx={{
            width: `calc(100vw - ${paddingLeft})`,
            position: 'fixed',
            left: paddingLeft,
            top: '62px',
            zIndex: theme.zIndex.drawer,
            background: theme.palette.primary.dark,
          }}
        >
          <Tabs
            value={pageState.subNavigation.some((nav) => isPartialMatch(nav.routeProps.path ?? '')) ? location.pathname : false}
            indicatorColor="secondary"
            textColor="inherit"
            variant="fullWidth"
            aria-label="mobile navigation"
            sx={{ minHeight: 48, '& .MuiTab-root': { minHeight: 48 } }}
          >
            {pageState.subNavigation.map((nav) => (
              <Tab
                key={nav.title}
                icon={<nav.iconComponent />}
                iconPosition="start"
                value={isPartialMatch(nav.routeProps.path ?? '') ? location.pathname : `NO-MATCH-${nav.routeProps.path ?? ''}`}
                label={isLarge ? nav.title : ''}
                component={RouterLink}
                to={{ pathname: nav.routeProps.path ?? '', search: tabSearch }}
              />
            ))}
          </Tabs>
        </Box>
      )}
    </>
  );
};

export default TopBar;
